import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup';
import { Moment } from 'moment-timezone';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { RecurrenceEnd } from 'model/RecurringSeries';
import { getMaxSeriesEndDate } from '../../utils/recurringSeries';
import StepSection from './StepSection';
import styles from './RecurringSeriesSteps.module.scss';

interface EndsStepProps {
    end: RecurrenceEnd;
    anchorDate: Moment | undefined;
    timeZone: string;
    onChangeEnd: (end: RecurrenceEnd) => void;
}

type RecurrenceEndType = RecurrenceEnd['type'];

const DEFAULT_BOOKINGS = 2;

const options: Array<{ value: RecurrenceEndType; inlineLabel: string }> = [
    { value: 'never', inlineLabel: 'Indefinitely' },
    { value: 'after', inlineLabel: 'After' },
    { value: 'on_date', inlineLabel: 'On Date' }
];

export const EndsStep: FunctionComponent<EndsStepProps> = props => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [lastBookings, setLastBookings] = useState(
        props.end.type === 'after' ? props.end.bookings : DEFAULT_BOOKINGS
    );
    const [lastDate, setLastDate] = useState<Moment | undefined>(
        props.end.type === 'on_date' ? props.end.date : undefined
    );

    const bookings = props.end.type === 'after' ? props.end.bookings : undefined;
    const endDate = props.end.type === 'on_date' ? props.end.date : undefined;

    const changeType = (type: RecurrenceEndType) => {
        switch (type) {
            case 'never':
                props.onChangeEnd({ type: 'never' });
                break;
            case 'after':
                props.onChangeEnd({ type: 'after', bookings: lastBookings });
                break;
            case 'on_date':
                props.onChangeEnd({ type: 'on_date', date: lastDate });
                break;
        }
    };

    const changeBookings = (event: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(event.target.value, 10);
        const value = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
        setLastBookings(value);
        props.onChangeEnd({ type: 'after', bookings: value });
    };

    const selectDate = (date: Moment | null) => {
        setCalendarOpen(false);
        setLastDate(date ?? undefined);
        props.onChangeEnd({ type: 'on_date', date: date ?? undefined });
    };

    return (
        <StepSection
            number={2}
            title="Ends"
        >
            <RadioGroup<RecurrenceEndType>
                options={options}
                value={props.end.type}
                className={styles.endsRow}
                onChange={changeType}
            />

            {props.end.type === 'after' && (
                <div className={styles.countRow}>
                    <input
                        min={1}
                        step={1}
                        type="number"
                        value={bookings ?? ''}
                        className={styles.countField}
                        onChange={changeBookings}
                    />
                    <span className={styles.countUnit}>Bookings</span>
                </div>
            )}

            {props.end.type === 'on_date' && (
                <Dropdown
                    open={calendarOpen}
                    onOpenChange={setCalendarOpen}
                >
                    <DropdownTrigger>
                        <div className={clsx(styles.field, styles.dateField)}>
                            <span className={clsx(styles.fieldValue, { [styles.fieldPlaceholder]: !endDate })}>
                                {endDate ? endDate.format('MMM D, YYYY') : 'Select date'}
                            </span>
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className={styles.fieldIcon}
                            />
                        </div>
                    </DropdownTrigger>
                    <DropdownContent
                        align="start"
                        zIndex={1301}
                        maxHeight={420}
                        className={styles.calendarDropdown}
                    >
                        <DatePicker
                            timeZone={props.timeZone}
                            value={endDate ?? null}
                            minDate={props.anchorDate?.clone().add(1, 'day')}
                            maxDate={props.anchorDate && getMaxSeriesEndDate(props.anchorDate)}
                            onChange={selectDate}
                        />
                    </DropdownContent>
                </Dropdown>
            )}
        </StepSection>
    );
};

export default EndsStep;
